// Masyu generator & validator
function combCycle(R, C) {
  const path = [];
  for (let r = 0; r < R; r++) path.push([r, 0]);
  for (let c = 1; c < C; c++) {
    if (c % 2 === 1) for (let r = R - 1; r >= 1; r--) path.push([r, c]);
    else for (let r = 1; r <= R - 1; r++) path.push([r, c]);
  }
  for (let c = C - 1; c >= 1; c--) path.push([0, c]);
  return path;
}

function transform(path, n) {
  let p = path.map(([r, c]) => [r, c]);
  if (Math.random() < 0.5) p = p.map(([r, c]) => [c, r]); // transpose
  if (Math.random() < 0.5) p = p.map(([r, c]) => [n - 1 - r, c]);
  if (Math.random() < 0.5) p = p.map(([r, c]) => [r, n - 1 - c]);
  return p;
}

const edgeKey = (a, b) => {
  const [r1, c1] = a, [r2, c2] = b;
  if (r1 === r2) return `H:${r1}:${Math.min(c1, c2)}`;
  return `V:${Math.min(r1, r2)}:${c1}`;
};

export function makeMasyu(n) {
  const path = transform(combCycle(n, n), n);
  const K = path.length;
  const idxAt = {};
  path.forEach(([r, c], i) => { idxAt[`${r},${c}`] = i; });
  const dirOf = (from, to) => [to[0] - from[0], to[1] - from[1]];
  const isTurn = (i) => {
    const prev = path[(i - 1 + K) % K], cur = path[i], next = path[(i + 1) % K];
    const din = dirOf(prev, cur), dout = dirOf(cur, next);
    return din[0] !== dout[0] || din[1] !== dout[1];
  };
  const pearls = [];
  const used = new Set();
  const order = [...Array(K).keys()].sort(() => Math.random() - 0.5);
  const target = Math.max(4, Math.floor(K * 0.14));
  for (const i of order) {
    if (pearls.length >= target) break;
    const [r, c] = path[i];
    const k = `${r},${c}`;
    if (used.has(k)) continue;
    const turn = isTurn(i);
    const prevTurn = isTurn((i - 1 + K) % K);
    const nextTurn = isTurn((i + 1) % K);
    if (!turn && (prevTurn || nextTurn)) { pearls.push({ r, c, type: "W" }); used.add(k); }
    else if (turn && !prevTurn && !nextTurn) { pearls.push({ r, c, type: "B" }); used.add(k); }
  }
  const solEdges = new Set();
  for (let i = 0; i < K; i++) solEdges.add(edgeKey(path[i], path[(i + 1) % K]));
  return { n, pearls, solEdges: [...solEdges] };
}

// edges: Set of keys "H:r:c" (between (r,c)-(r,c+1)) or "V:r:c" (between (r,c)-(r+1,c))
function cellEdges(r, c, edges, n) {
  const e = [];
  if (c > 0 && edges.has(`H:${r}:${c - 1}`)) e.push("L");
  if (c < n - 1 && edges.has(`H:${r}:${c}`)) e.push("R");
  if (r > 0 && edges.has(`V:${r - 1}:${c}`)) e.push("U");
  if (r < n - 1 && edges.has(`V:${r}:${c}`)) e.push("D");
  return e;
}

function cellType(r, c, edges, n) {
  const e = cellEdges(r, c, edges, n);
  if (e.length !== 2) return null;
  const set = new Set(e);
  if ((set.has("L") && set.has("R")) || (set.has("U") && set.has("D"))) return "straight";
  return "turn";
}

const DIR = { L: [0, -1], R: [0, 1], U: [-1, 0], D: [1, 0] };

export function masyuWin(edgesArr, puzzle) {
  const { n, pearls } = puzzle;
  const edges = new Set(edgesArr);
  // degree 0 or 2, count loop cells
  const loopCells = [];
  for (let r = 0; r < n; r++)
    for (let c = 0; c < n; c++) {
      const d = cellEdges(r, c, edges, n).length;
      if (d !== 0 && d !== 2) return false;
      if (d === 2) loopCells.push([r, c]);
    }
  if (loopCells.length === 0) return false;
  // single loop: walk from a loop cell following edges, must return after visiting all loop cells
  const start = loopCells[0];
  let cur = start, prev = null, steps = 0;
  do {
    const e = cellEdges(cur[0], cur[1], edges, n);
    let moved = false;
    for (const d of e) {
      const [dr, dc] = DIR[d];
      const nc = [cur[0] + dr, cur[1] + dc];
      if (prev && nc[0] === prev[0] && nc[1] === prev[1]) continue;
      prev = cur; cur = nc; moved = true; break;
    }
    if (!moved) return false;
    steps++;
    if (steps > loopCells.length + 1) return false;
  } while (!(cur[0] === start[0] && cur[1] === start[1]));
  if (steps !== loopCells.length) return false;
  // pearls
  for (const p of pearls) {
    const type = cellType(p.r, p.c, edges, n);
    if (type === null) return false;
    const e = cellEdges(p.r, p.c, edges, n);
    const neighborTypes = e.map((d) => {
      const [dr, dc] = DIR[d];
      return cellType(p.r + dr, p.c + dc, edges, n);
    });
    if (p.type === "W") {
      if (type !== "straight") return false;
      if (!neighborTypes.some((t) => t === "turn")) return false;
    } else {
      if (type !== "turn") return false;
      if (!neighborTypes.every((t) => t === "straight")) return false;
    }
  }
  return true;
}

export { edgeKey, cellType };
